import React, { useState } from 'react';
import { ShieldAlert, Volume2, Bell, MapPin, Mail, X, Radio } from 'lucide-react';

const HELI_LS_KEY = 'tucsonstreets:helicopter_overlay';
const HELI_PREF_EVENT = 'tg:heli-pref';
function readHeliPref(): boolean {
  try { return localStorage.getItem(HELI_LS_KEY) !== 'false'; } catch { return true; }
}
function writeHeliPref(v: boolean) {
  try {
    localStorage.setItem(HELI_LS_KEY, String(v));
    window.dispatchEvent(new CustomEvent(HELI_PREF_EVENT, { detail: v }));
  } catch {}
}

interface AlarmSettings {
  audioOn: boolean;
  proximityAlarmOn: boolean;
  alertSound: string;
  alarmSound: string;
  proximityRadius: number;
  alarmType: 'Mute' | 'Tone' | 'Alarm' | 'Visual Alarm';
  visualAlarmFlash: boolean;
  notificationEmails: string;
  includeFullData: boolean;
}

interface Props {
  onClose: () => void;
  dangerRadius: number;
  setDangerRadius: (radius: number) => void;
}

export default function AlarmSettingsPanel({ onClose, dangerRadius, setDangerRadius }: Props) {
  const [heliOverlay, setHeliOverlay] = useState<boolean>(readHeliPref);
  const [settings, setSettings] = useState<AlarmSettings>({
    audioOn: true,
    proximityAlarmOn: true,
    alertSound: 'Beep',
    alarmSound: 'Siren',
    alarmType: 'Mute',
    visualAlarmFlash: false,
    notificationEmails: '',
    includeFullData: false,
  });

  const updateSetting = (key: keyof AlarmSettings, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="h-full flex flex-col bg-[#0c0a09] border-l border-amber-900/30 text-amber-500 font-mono">
      <div className="p-3 border-b border-amber-900/30 flex justify-between items-center bg-[#0c0a09]">
        <h2 className="text-sm font-bold text-amber-500 uppercase tracking-widest flex items-center gap-2">
          <ShieldAlert size={14} /> Proximity Alarm
        </h2>
        <button onClick={onClose} className="p-1 rounded text-amber-400 hover:text-amber-400"><X size={14} /></button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6 text-xs">
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span>Audio</span>
            <input type="checkbox" checked={settings.audioOn} onChange={(e) => updateSetting('audioOn', e.target.checked)} className="accent-amber-600" />
          </div>
          <div className="flex justify-between items-center">
            <span>Proximity Alarm</span>
            <input type="checkbox" checked={settings.proximityAlarmOn} onChange={(e) => updateSetting('proximityAlarmOn', e.target.checked)} className="accent-amber-600" />
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-amber-400">Alert Audio Sounds</label>
          <select value={settings.alertSound} onChange={(e) => updateSetting('alertSound', e.target.value)} className="w-full terminal-input p-2 rounded-sm">
            {['Beep', 'Chime', 'Ping', 'Pulse', 'Snap'].map(s => <option key={s}>{s}</option>)}
          </select>
        </div>

        <div className="space-y-2">
          <label className="block text-amber-400">Alarm Audio Sounds</label>
          <select value={settings.alarmSound} onChange={(e) => updateSetting('alarmSound', e.target.value)} className="w-full terminal-input p-2 rounded-sm">
            {['Siren', 'Klaxon', 'Warning Loop'].map(s => <option key={s}>{s}</option>)}
          </select>
        </div>

        <div className="space-y-2">
          <label className="block text-amber-400">Proximity Radius: {dangerRadius} miles</label>
          <input type="range" min="0.25" max="5" step="0.25" value={dangerRadius} onChange={(e) => setDangerRadius(Number(e.target.value))} className="w-full accent-amber-600" />
        </div>

        <div className="space-y-2">
          <label className="block text-amber-400">Proximity Alarm Type</label>
          <select value={settings.alarmType} onChange={(e) => updateSetting('alarmType', e.target.value)} className="w-full terminal-input p-2 rounded-sm">
            {['Mute', 'Tone', 'Alarm', 'Visual Alarm'].map(t => <option key={t}>{t}</option>)}
          </select>
        </div>

        <div className="flex justify-between items-center">
          <span>Flash UI until acknowledged</span>
          <input type="checkbox" checked={settings.visualAlarmFlash} onChange={(e) => updateSetting('visualAlarmFlash', e.target.checked)} className="accent-amber-600" />
        </div>

        <div className="space-y-2">
          <label className="block text-amber-400">Notification Emails</label>
          <input type="text" value={settings.notificationEmails} onChange={(e) => updateSetting('notificationEmails', e.target.value)} placeholder="email1@example.com, email2@example.com" className="w-full terminal-input p-2 rounded-sm" />
        </div>

        <div className="flex justify-between items-center">
          <span>Include full alert data</span>
          <input type="checkbox" checked={settings.includeFullData} onChange={(e) => updateSetting('includeFullData', e.target.checked)} className="accent-amber-600" />
        </div>

        <div className="border-t border-amber-900/30 pt-5 space-y-2">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-red-400 mb-1.5">
                <Radio size={12} />
                <span className="uppercase tracking-widest text-[11px] font-bold">PIGAIR1 Air Support Overlay</span>
              </div>
              <p className="text-amber-700/90 text-[10.5px] leading-relaxed">
                When ON, the map polls live ADS-B from adsb.lol every 15 seconds and renders any Tucson PD helicopter currently airborne as a red rotor marker labelled <span className="text-red-400 font-bold">PIGAIR1</span>. Click the marker for altitude, heading, speed, and callsign &mdash; useful during pursuits, perimeter ops, or when you hear rotors and want to know who&rsquo;s up. Turn OFF if you&rsquo;re on cellular and want to save bandwidth (the poll is roughly 2&nbsp;KB per request, cached server-side). Default: ON.
              </p>
            </div>
            <input
              type="checkbox"
              checked={heliOverlay}
              onChange={(e) => { const v = e.target.checked; setHeliOverlay(v); writeHeliPref(v); }}
              className="accent-red-500 mt-1 shrink-0 w-4 h-4"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
